import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';

interface OccupancyLineGraphProps {
  data: Array<{
    date: string; // YYYY-MM-DD
    occupancy: number;
    isPrelim?: boolean;
  }>;
  height?: number;
  graphRef?: any;
}

const formatMonthYear = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
};

const formatTooltipTitle = (dateStr: string) => {
  const date = new Date(dateStr);
  // Sun, 6 Apr, 2025
  return date.toLocaleString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// For x-axis: only show the first tick of each month
const getXAxisTicks = (data: any[]) => {
  const seen = new Set();
  return data
    .filter(({ date }) => {
      const d = new Date(date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ date }) => date);
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const { date, occupancy, isPrelim } = payload[0].payload;
    return (
      <Box sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          {formatTooltipTitle(date)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2 }}>
          Occupancy Rate: {occupancy}%
        </Typography>
        {isPrelim && (
          <Typography variant="caption" color="warning.main">
            Preliminary Data
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

const OccupancyLineGraph: React.FC<OccupancyLineGraphProps> = ({
  data,
  height = 220,
  graphRef,
}) => {
  // For x-axis: only show the first tick of each month
  const xAxisTicks = getXAxisTicks(data);

  return (
    <Box sx={{ width: '100%', height }} ref={graphRef}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            ticks={xAxisTicks}
            tickFormatter={formatMonthYear}
            style={{ fontFamily: 'Roboto', fontSize: '12px' }}
            tickLine={false}
            minTickGap={20}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={v => `${v}%`}
            style={{ fontFamily: 'Roboto', fontSize: '12px' }}
            axisLine={false}
            tickLine={false}
            label={{
              value: 'Occupancy Rate (%)',
              angle: -90,
              position: 'insideLeft',
              offset: 0,
              style: {
                textAnchor: 'middle',
                dominantBaseline: 'middle',
                fontSize: '0.75rem',
                fontFamily: 'Roboto',
              },
            }}
          />
          <Tooltip content={<CustomTooltip />} position={{ y: -50 }} />
          <Line
            type="monotone"
            dataKey="occupancy"
            stroke={COLOR_PRIMARY_2}
            strokeWidth={3}
            dot={({ cx, cy, payload }) => {
              const date = new Date(payload.date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              if (!isWeekend) return null;
              return (
                <circle
                  key={`dot-${payload.date}`}
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill={COLOR_PRIMARY_2}
                  stroke="#fff"
                  strokeWidth={1}
                />
              );
            }}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default OccupancyLineGraph;
